// Full-screen "present" overlay for the check-in QR (mockup present()/closePresent();
// logic mirrors screens/EventQRDisplayScreen.js). Shows the event title, the club
// logo + name, and the QR (owner decision Oct 15) plus a close button.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import Icon from "../../../components/Icon";
import { ClubLogo, Spinner, Button } from "../../../components/primitives";
import { getEventEndTime } from "../../../lib/format";
import { AttendanceService } from "../../../../../services/attendanceService";
import { EventService } from "../../../../../services/eventService";
import { get as getCachedQR, set as setCachedQR, msUntilRotation } from "../../../../../utils/qrTokenPrefetch";
import { msg } from "./eventUtils";

const WINDOW_CHECK_INTERVAL_MS = 20 * 1000;

export default function CheckinQR({ event, club, onClose }) {
  const eventId = event?.id;
  const cached = useRef(eventId ? getCachedQR(eventId) : null);
  const [payload, setPayload] = useState(cached.current?.payload || null);
  const [generatedAt, setGeneratedAt] = useState(cached.current?.generatedAt ?? null);
  const [loading, setLoading] = useState(!cached.current);
  const [errorText, setErrorText] = useState("");
  const [windowClosed, setWindowClosed] = useState(false);
  const [eventWindow, setEventWindow] = useState(null);
  const [rotateNonce, setRotateNonce] = useState(0);
  const [svg, setSvg] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    seq.current += 1;
    return () => { seq.current += 1; };
  }, [eventId]);

  const loadToken = useCallback(async ({ silent = false, background = false } = {}) => {
    const mySeq = seq.current;
    if (!silent) setLoading(true);
    try {
      const { data, error } = await AttendanceService.generateEventQRToken(eventId);
      if (mySeq !== seq.current) return;
      if (error) {
        if (error.code === "checkin_window_closed") {
          setWindowClosed(true);
          setPayload(null);
        } else {
          setWindowClosed(false);
          if (background) return; // keep the code on screen; the rotation timer retries
          setErrorText(msg(error, "Could not generate a check-in QR code."));
          setPayload(null);
        }
      } else {
        setWindowClosed(false);
        setErrorText("");
        setPayload(data?.payload || null);
        setGeneratedAt(data?.generated_at ?? null);
        setCachedQR(eventId, data?.payload, data?.generated_at);
      }
    } finally {
      if (mySeq === seq.current) {
        setLoading(false);
        setRotateNonce((n) => n + 1);
      }
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) loadToken({ silent: !!cached.current });
  }, [eventId, loadToken]);

  // Re-mint just after the shown token's expiry (timed from the server generated_at).
  useEffect(() => {
    if (!eventId || windowClosed || !payload) return undefined;
    const id = setTimeout(() => loadToken({ silent: true, background: true }), msUntilRotation(generatedAt));
    return () => clearTimeout(id);
  }, [eventId, windowClosed, payload, generatedAt, rotateNonce, loadToken]);

  // Event window: fetched once, then a local time check every 20 s.
  useEffect(() => {
    let cancelled = false;
    if (eventId) {
      EventService.getEventById(eventId).then(({ data, error }) => {
        if (cancelled || error || !data?.start_date) return;
        setEventWindow({ start_date: data.start_date, end_date: data.end_date });
      });
    }
    return () => { cancelled = true; };
  }, [eventId]);

  useEffect(() => {
    if (!eventWindow) return undefined;
    const id = setInterval(() => {
      const end = getEventEndTime(eventWindow);
      if (end && Date.now() >= end.getTime()) setWindowClosed(true);
    }, WINDOW_CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [eventWindow]);

  // Render the payload as an SVG string (qrcode output is generated markup, not user HTML).
  useEffect(() => {
    let cancelled = false;
    if (!payload) { setSvg(""); return undefined; }
    QRCode.toString(payload, { type: "svg", margin: 0, errorCorrectionLevel: "M" })
      .then((s) => { if (!cancelled) setSvg(s); })
      .catch(() => { if (!cancelled) setSvg(""); });
    return () => { cancelled = true; };
  }, [payload]);

  // Escape closes; try browser fullscreen like the mockup.
  const rootRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey);
    try { rootRef.current?.requestFullscreen?.().catch(() => {}); } catch { /* ignore */ }
    rootRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      try { if (document.fullscreenElement) document.exitFullscreen(); } catch { /* ignore */ }
    };
  }, [onClose]);

  // Leaving fullscreen with the browser's own Esc should also close the overlay.
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement && rootRef.current?.dataset.fs) onClose(); };
    const mark = () => { if (document.fullscreenElement && rootRef.current) rootRef.current.dataset.fs = "1"; };
    document.addEventListener("fullscreenchange", mark);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", mark);
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, [onClose]);

  return createPortal(
    <div className="present" ref={rootRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Check-in QR" data-testid="checkinQr">
      <button
        type="button"
        className="chip glass"
        style={{ position: "absolute", top: "calc(20px + env(safe-area-inset-top,0px))", right: "calc(20px + env(safe-area-inset-right,0px))" }}
        onClick={onClose}
        data-testid="checkinQr.close"
        aria-label="Close check-in QR"
      >
        <Icon name="x" size={16} />Close
      </button>
      <div className="row" style={{ gap: 12 }}>
        <ClubLogo club={club} size={44} />
        <b style={{ fontSize: 18 }}>{club?.name}</b>
      </div>
      <h1 style={{ fontFamily: "var(--display)", fontSize: "clamp(28px,5vw,52px)", lineHeight: 1 }}>{event?.title}</h1>
      {windowClosed ? (
        <div className="ev-closed">
          <Icon name="clock" size={40} />
          <b style={{ fontSize: 18 }}>Check-In Window Closed</b>
          <p>This event&apos;s check-in window has ended, so this QR code can no longer be used.</p>
        </div>
      ) : errorText ? (
        <div className="ev-closed">
          <Icon name="alert" size={40} />
          <b style={{ fontSize: 18 }}>QR Error</b>
          <p>{errorText}</p>
          <Button variant="pri" size="sm" onClick={() => { setErrorText(""); loadToken(); }} data-testid="checkinQr.retry">Retry</Button>
        </div>
      ) : (
        <div className="qr ev-qr-wrap" aria-label="Check-in QR code">
          {loading || !svg ? <Spinner large label="Loading check-in code" /> : <div className="ev-qr-svg" dangerouslySetInnerHTML={{ __html: svg }} />}
        </div>
      )}
    </div>,
    document.body
  );
}
